import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Link,
    MenuItem,
    Paper,
    Popover,
    Slide,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Title, useNotify } from 'react-admin';
import { DatePicker, InputField } from '@/components/common';
import { CURRENCIES } from '@/pages/components/common/currencyData';
import {
    AdminCurrencyRateDailyVO,
    AdminCurrencyVO,
    CurrencyRateDailyPage,
    CurrencyRateService,
} from '@/services/manageService/currencyRateService';
import { convertToDisplayFormat, formatDDMMYYYY, parseDDMMYYYY } from '@/utils/dataUtils';
import upSvg from '@/assets/icons/up.svg';
import downSvg from '@/assets/icons/down.svg';

/** HKMA monthly statistical bulletin (Chinese). */
const HKMA_BULLETIN_URL =
    'https://www.hkma.gov.hk/chi/data-publications-and-research/data-and-statistics/monthly-statistical-bulletin/';

/** Default page size � align with FsList. */
const DEFAULT_PAGE_SIZE = 25;

/** Trend colors (match up.svg / down.svg fills). */
const RATE_UP_COLOR = '#4ECB73';
const RATE_DOWN_COLOR = '#d81e06';

/** Empty MP page shell. */
const EMPTY_PAGE: CurrencyRateDailyPage = {
    records: [],
    total: 0,
    size: DEFAULT_PAGE_SIZE,
    current: 1,
    pages: 0,
};

/** Day-over-day trend vs older row on the same page. */
type RateTrend = 'up' | 'down' | 'flat' | null;

/** Format rate date for display (DD/MM/YYYY). */
function toDisplayDate(iso?: string): string {
    if (!iso) return '-';
    return convertToDisplayFormat(iso) || iso;
}

/** Format rate cell. */
function formatRate(v: number | string | undefined): string {
    if (v === undefined || v === null || v === '') return '-';
    return String(v);
}

/** Parse rate to number; null if missing / NaN. */
function toRateNumber(v: number | string | undefined): number | null {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/** Trim float noise for popover amounts. */
function formatPopoverAmount(n: number): string {
    if (!Number.isFinite(n)) return '-';
    const rounded = Math.round(n * 1e8) / 1e8;
    return String(rounded);
}

/**
 * Popover parts: HKD first, target currency after (colon rendered bold in UI).
 * Stored rate = 1 CODE -> rate HKD; if either side of the ratio is < 1, scale both by 100.
 */
function formatRatePopoverParts(
    code: string,
    rate: number | string | undefined,
): { hkdPart: string; fxPart: string } | null {
    const n = toRateNumber(rate);
    if (n === null || n <= 0) return null;
    let hkdAmt = n;
    let fxAmt = 1;
    if (hkdAmt < 1 || fxAmt < 1) {
        hkdAmt *= 100;
        fxAmt *= 100;
    }
    return {
        hkdPart: `${formatPopoverAmount(hkdAmt)} HKD`,
        fxPart: `${formatPopoverAmount(fxAmt)} ${code}`,
    };
}

/**
 * Compare current vs previous (older) day rate.
 * @returns up / down / flat / null (no comparable previous)
 */
function rateTrend(
    current: number | string | undefined,
    previous: number | string | undefined,
): RateTrend {
    const cur = toRateNumber(current);
    const prev = toRateNumber(previous);
    if (cur === null || prev === null) return null;
    if (cur > prev) return 'up';
    if (cur < prev) return 'down';
    return 'flat';
}

/** Static symbol for header � avoid DB mojibake. */
function symbolOf(code: string): string | undefined {
    return CURRENCIES.find((c) => c.code === code)?.symbol;
}

/** Header: `USD ($)` when symbol known. */
function headerCurrencyLabel(code: string): string {
    const sym = symbolOf(code);
    return sym ? `${code} (${sym})` : code;
}

/** Select label: code + English name only. */
function currencyOptionLabel(c: AdminCurrencyVO): string {
    const code = c.currencyCode ?? '';
    const name = (c.englishName ?? '').trim();
    return name ? `${code} - ${name}` : code;
}

/**
 * Tools > Currency & Rate: paginated daily FX table (1 day = 1 row).
 * Layout mirrors FsList (single header row + Paper table filling remaining height).
 */
export default function CurrencyRatePage() {
    const theme = useTheme();
    const notify = useNotify();
    /** Draft filters (form). */
    const [currencyCode, setCurrencyCode] = useState('');
    const [rateDate, setRateDate] = useState('');
    /** Applied filters (last Search / init). */
    const [appliedCurrency, setAppliedCurrency] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CurrencyRateDailyPage>(EMPTY_PAGE);
    /** Currencies that have HKMA rate rows in DB. */
    const [rateCurrencies, setRateCurrencies] = useState<AdminCurrencyVO[]>([]);
    /** Hover popover on currency rate cells. */
    const [ratePopover, setRatePopover] = useState<{
        anchorEl: HTMLElement;
        hkdPart: string;
        fxPart: string;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const list = await CurrencyRateService.listCurrencies(false);
                if (cancelled) return;
                setRateCurrencies(
                    (list ?? []).filter((c) => Number(c.hasHkmaRate) === 1 && Boolean(c.currencyCode)),
                );
            } catch (e: unknown) {
                if (cancelled) return;
                const msg = e instanceof Error ? e.message : 'Failed to load currencies';
                notify(msg, { type: 'error' });
                setRateCurrencies([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [notify]);

    const fetchPage = useCallback(
        async (pageNum: number, pageSize: number, currency: string, date: string) => {
            setLoading(true);
            setError(null);
            try {
                const res = await CurrencyRateService.pageCurrencyRates({
                    pageNum,
                    pageSize,
                    currencyCode: currency || undefined,
                    rateDate: date || undefined,
                });
                setData(res ?? EMPTY_PAGE);
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Query failed';
                setError(msg);
                setData(EMPTY_PAGE);
                notify(msg, { type: 'error' });
            } finally {
                setLoading(false);
            }
        },
        [notify],
    );

    useEffect(() => {
        void fetchPage(pageIndex + 1, rowsPerPage, appliedCurrency, appliedDate);
    }, [pageIndex, rowsPerPage, appliedCurrency, appliedDate, fetchPage]);

    const onSearch = useCallback(() => {
        setAppliedCurrency(currencyCode.trim());
        setAppliedDate(rateDate.trim());
        setPageIndex(0);
    }, [currencyCode, rateDate]);

    /** Column codes from current page rows (union, sorted). */
    const columns = useMemo(() => {
        const set = new Set<string>();
        for (const row of data.records ?? []) {
            Object.keys(row.rates ?? {}).forEach((c) => set.add(c));
        }
        return Array.from(set).sort();
    }, [data.records]);

    const highlight = appliedCurrency.toUpperCase();
    const colSpan = Math.max(columns.length, 1) + 1;
    const records = data.records ?? [];
    /** DD/MM/YYYY today � DatePicker maxDate/toDate require this format (not yyyy-MM-dd). */
    const todayDdMmYyyy = formatDDMMYYYY(new Date());

    const currencyOptions = useMemo(() => {
        return [...rateCurrencies].sort((a, b) => {
            const ra = Number(a.popularRank ?? 9999);
            const rb = Number(b.popularRank ?? 9999);
            if (ra !== rb) return ra - rb;
            return String(a.currencyCode).localeCompare(String(b.currencyCode));
        });
    }, [rateCurrencies]);

    const currencyLabelByCode = useMemo(() => {
        const map = new Map<string, string>();
        for (const c of currencyOptions) {
            if (c.currencyCode) map.set(c.currencyCode, currencyOptionLabel(c));
        }
        return map;
    }, [currencyOptions]);

    const closeRatePopover = useCallback(() => setRatePopover(null), []);

    const openRatePopover = useCallback(
        (event: MouseEvent<HTMLElement>, code: string, rate: number | string | undefined) => {
            const parts = formatRatePopoverParts(code, rate);
            if (!parts) return;
            setRatePopover({
                anchorEl: event.currentTarget,
                hkdPart: parts.hkdPart,
                fxPart: parts.fxPart,
            });
        },
        [],
    );

    const renderRateCell = (
        code: string,
        rowIndex: number,
        row: AdminCurrencyRateDailyVO,
        isHi: boolean,
    ) => {
        const rate = row.rates?.[code];
        // Records are DESC by date: next row = older trading day on this page
        const older = records[rowIndex + 1]?.rates?.[code];
        const trend = rateTrend(rate, older);
        const hasRate = rate !== undefined && rate !== null && rate !== '';

        let color: string | undefined;
        if (trend === 'up') color = RATE_UP_COLOR;
        else if (trend === 'down') color = RATE_DOWN_COLOR;

        return (
            <TableCell
                key={code}
                align="center"
                onMouseEnter={hasRate ? (e) => openRatePopover(e, code, rate) : undefined}
                onMouseLeave={hasRate ? closeRatePopover : undefined}
                sx={{
                    ...(isHi
                        ? {
                              bgcolor: 'action.selected',
                              fontWeight: 700,
                          }
                        : undefined),
                    cursor: hasRate ? 'default' : undefined,
                }}
            >
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {trend === 'up' && (
                        <Box
                            component="img"
                            src={upSvg}
                            alt="up"
                            sx={{ width: 14, height: 14, flexShrink: 0 }}
                        />
                    )}
                    {trend === 'down' && (
                        <Box
                            component="img"
                            src={downSvg}
                            alt="down"
                            sx={{ width: 14, height: 14, flexShrink: 0 }}
                        />
                    )}
                    <Box component="span" sx={color ? { color, fontWeight: 600 } : undefined}>
                        {formatRate(rate)}
                    </Box>
                </Stack>
            </TableCell>
        );
    };

    const renderBody = () => {
        if (loading) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        <CircularProgress size={28} sx={{ my: 2 }} />
                    </TableCell>
                </TableRow>
            );
        }
        if (error) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() =>
                                        void fetchPage(pageIndex + 1, rowsPerPage, appliedCurrency, appliedDate)
                                    }
                                >
                                    Retry
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    </TableCell>
                </TableRow>
            );
        }
        if (!records.length) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        No rate data for the current filters.
                    </TableCell>
                </TableRow>
            );
        }
        return records.map((row: AdminCurrencyRateDailyVO, rowIndex: number) => (
            <TableRow key={row.id ?? row.rateDate} hover>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {toDisplayDate(row.rateDate)}
                </TableCell>
                {columns.map((code) => {
                    const isHi = Boolean(highlight) && code === highlight;
                    return renderRateCell(code, rowIndex, row, isHi);
                })}
            </TableRow>
        ));
    };

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    p: '20px',
                    height: 'calc(100vh - 120px)',
                    minHeight: '70vh',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Title title="Currency & Rate" />
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexShrink={0}
                    gap={2}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CurrencyExchangeOutlinedIcon color="primary" />
                            <Typography variant="h6" fontWeight={700}>
                                Currency & Rate
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Exchange rate data sourced from the{' '}
                            <Link href={HKMA_BULLETIN_URL} target="_blank" rel="noopener noreferrer">
                                Hong Kong Monetary Authority
                            </Link>
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                        <Box sx={{ minWidth: 200 }}>
                            <InputField
                                select
                                size="small"
                                label="Currency"
                                value={currencyCode}
                                onChange={(e) => setCurrencyCode(String(e.target.value ?? ''))}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => {
                                        const code = String(selected ?? '');
                                        if (!code) return '';
                                        return currencyLabelByCode.get(code) ?? code;
                                    },
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                align="left"
                            >
                                <MenuItem dense value="">
                                    <em>All</em>
                                </MenuItem>
                                {currencyOptions.map((c) => (
                                    <MenuItem dense key={c.currencyCode} value={c.currencyCode}>
                                        {currencyOptionLabel(c)}
                                    </MenuItem>
                                ))}
                            </InputField>
                        </Box>
                        <Box sx={{ minWidth: 160 }}>
                            <DatePicker
                                label="Date"
                                size="small"
                                value={rateDate || null}
                                onChange={(v) => {
                                    if (!v) {
                                        setRateDate('');
                                        return;
                                    }
                                    // Clamp typed/pasted values to today or earlier
                                    const picked = parseDDMMYYYY(v);
                                    const today = parseDDMMYYYY(todayDdMmYyyy);
                                    if (picked && today && picked.getTime() > today.getTime()) {
                                        return;
                                    }
                                    setRateDate(v);
                                }}
                                maxDate={todayDdMmYyyy}
                                toDate={todayDdMmYyyy}
                                fullWidth
                            />
                        </Box>
                        <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={
                                loading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <SearchOutlinedIcon />
                                )
                            }
                            onClick={onSearch}
                            disabled={loading}
                            sx={{
                                borderRadius: theme.shape.borderRadius,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: theme.shadows[1],
                                '&:hover': { boxShadow: theme.shadows[3] },
                            }}
                        >
                            Search
                        </Button>
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 500 }}>
                                    Date
                                </TableCell>
                                {columns.map((code) => {
                                    const isHi = Boolean(highlight) && code === highlight;
                                    return (
                                        <TableCell
                                            key={code}
                                            align="center"
                                            sx={
                                                isHi
                                                    ? {
                                                          bgcolor: 'action.selected',
                                                          fontWeight: 700,
                                                      }
                                                    : { fontWeight: 500 }
                                            }
                                        >
                                            {headerCurrencyLabel(code)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderBody()}</TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={data.total ?? 0}
                    page={pageIndex}
                    onPageChange={(_, p) => setPageIndex(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPageIndex(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ mt: 1, flexShrink: 0 }}
                />

                <Popover
                    open={Boolean(ratePopover)}
                    anchorEl={ratePopover?.anchorEl}
                    onClose={closeRatePopover}
                    disableRestoreFocus
                    sx={{ pointerEvents: 'none' }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    slotProps={{
                        paper: {
                            sx: { px: 1.5, py: 1, pointerEvents: 'none' },
                        },
                    }}
                >
                    <Typography variant="body2" component="div">
                        {ratePopover?.hkdPart}{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            :
                        </Box>{' '}
                        {ratePopover?.fxPart}
                    </Typography>
                </Popover>
            </Box>
        </Slide>
    );
}
